import axios from 'axios';
import {
  StudentSearchFilters,
  StudentSearchResponse,
  StudentDetailResponse,
} from '../types/student-search';

const API_BASE_URL = '/api/student-search';

// API エラーレスポンスの型定義
interface ApiErrorDetail {
  field: string;
  message: string;
  value?: any;
}

interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
    timestamp: string;
  };
}

/**
 * API エラーハンドリング（共通処理）
 */
const handleApiError = (error: unknown, defaultMessage: string): never => {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data as ApiErrorResponse | undefined;

    // バリデーションエラーの詳細メッセージを抽出
    if (
      errorResponse?.error?.details &&
      errorResponse.error.details.length > 0
    ) {
      const detailMessages = errorResponse.error.details
        .map((detail) => detail.message)
        .join('\n');
      throw new Error(detailMessages);
    }

    // 一般的なエラーメッセージ
    if (errorResponse?.error?.message) {
      throw new Error(errorResponse.error.message);
    }

    // HTTP ステータスコードに基づくエラーメッセージ
    switch (error.response?.status) {
      case 400:
        throw new Error('検索条件に問題があります');
      case 401:
        throw new Error('認証が必要です。ログインしてください');
      case 403:
        throw new Error('この機能を利用する権限がありません');
      case 404:
        throw new Error('指定されたリソースが見つかりません');
      case 500:
        throw new Error('サーバーエラーが発生しました');
      default:
        throw new Error(`API エラー: ${error.response?.status || 'unknown'}`);
    }
  }

  throw new Error(defaultMessage);
};

// 検索パラメータをクエリストリングに変換
const buildSearchParams = (filters: StudentSearchFilters): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.keyword) {
    params.append('keyword', filters.keyword);
  }
  if (filters.age_min !== undefined) {
    params.append('age_min', filters.age_min.toString());
  }
  if (filters.age_max !== undefined) {
    params.append('age_max', filters.age_max.toString());
  }
  if (filters.university_search) {
    params.append('university_search', filters.university_search);
  }
  if (filters.universities && filters.universities.length > 0) {
    params.append('universities', filters.universities.join(','));
  }
  if (filters.faculty_search) {
    params.append('faculty_search', filters.faculty_search);
  }
  if (filters.faculties && filters.faculties.length > 0) {
    params.append('faculties', filters.faculties.join(','));
  }
  if (filters.grades && filters.grades.length > 0) {
    params.append('grades', filters.grades.join(','));
  }
  if (filters.current_prefectures && filters.current_prefectures.length > 0) {
    params.append('current_prefectures', filters.current_prefectures.join(','));
  }
  if (filters.learning_start_date) {
    params.append('learning_start_date', filters.learning_start_date);
  }
  if (filters.learning_end_date) {
    params.append('learning_end_date', filters.learning_end_date);
  }
  if (filters.chat_frequency) {
    params.append('chat_frequency', filters.chat_frequency);
  }
  if (filters.learning_level) {
    params.append('learning_level', filters.learning_level);
  }
  if (filters.recent_activity_within_days) {
    params.append(
      'recent_activity_within_days',
      filters.recent_activity_within_days.toString()
    );
  }
  if (typeof filters.accepts_scout_emails === 'boolean') {
    params.append(
      'accepts_scout_emails',
      filters.accepts_scout_emails ? 'true' : 'false'
    );
  }
  if (filters.sort) {
    params.append('sort', filters.sort);
  }
  if (filters.order) {
    params.append('order', filters.order);
  }
  if (filters.page) {
    params.append('page', filters.page.toString());
  }

  return params;
};

export const studentSearchService = {
  // 学生検索・一覧取得
  async searchStudents(
    filters: StudentSearchFilters
  ): Promise<StudentSearchResponse> {
    try {
      const params = buildSearchParams(filters);
      const response = await axios.get(
        `${API_BASE_URL}/students?${params.toString()}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error, '学生検索に失敗しました');
    }
  },

  // 学生詳細取得
  async getStudentDetail(studentId: number): Promise<StudentDetailResponse> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students/${studentId}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error, '学生詳細の取得に失敗しました');
    }
  },
};
